import enhancedMatchContact from './enhancedMatchContact';
import getParticipantNames from './contact/getParticipantNames';

/**
 * Session Contact Enhancement Utility
 *
 * This utility is part of the enhanced contact matching solution that ensures contact names
 * persist through all message operations, particularly after send failures.
 *
 * Purpose:
 * - Replaces auto-generated custom contacts with real contacts from the contacts page
 * - Maintains contact information consistency across session updates
 * - Works in conjunction with enhancedMatchContact() for comprehensive contact resolution
 * - Handles both one-on-one and group SMS/chat sessions
 *
 * Usage Context:
 * - Called before Redux state updates in sendMessage operations
 * - Applied during session list fetching to enhance all sessions
 * - Used in UI components to display proper contact names
 *
 * @param {Object} session - The session object to enhance
 * @returns {Object} - Enhanced session object with real contact if found
 */
export default function enhanceSessionContact(session) {
  if (!session || !session.remote) {
    return session;
  }

  const isGroupSms = session.contactArr && session.contactArr.length > 1;
  const hasCustomContact = session.contactArr?.some((contact) => contact?.customContact === true);

  // Only enhance if there are custom contacts
  if (!hasCustomContact) {
    return session;
  }

  if (isGroupSms) {
    // Group SMS/Chat - enhance all contacts
    const phoneNumbers = session.remote.split(',').map((num) => num.trim());

    // Try to enhance each contact
    const enhancedContactArr = session.contactArr.map((contact, index) => {
      if (contact?.customContact === true && phoneNumbers[index]) {
        const betterContact = enhancedMatchContact(phoneNumbers[index]);
        if (betterContact && !betterContact.customContact) {
          return betterContact;
        }
      }
      return contact;
    });

    // Check if any contacts were actually enhanced
    const wasEnhanced = enhancedContactArr.some(
      (contact, index) => contact !== session.contactArr[index],
    );

    if (wasEnhanced) {
      return {
        ...session,
        contact: enhancedContactArr[0], // First contact becomes the primary contact
        contactArr: enhancedContactArr,
        tempContactArr: enhancedContactArr,
        partNames: getParticipantNames(enhancedContactArr), // Regenerate display names
      };
    }
  } else if (session.contact?.customContact === true) {
    // One-on-one SMS/Chat - enhance single contact
    const betterContact = enhancedMatchContact(session.remote);

    if (betterContact && !betterContact.customContact) {
      return {
        ...session,
        contact: betterContact,
        contactArr: [betterContact],
        tempContactArr: [betterContact],
      };
    }
  }

  return session;
}

/**
 * Enhances an array of sessions by replacing custom contacts with real ones when possible
 * @param {Array} sessions - Array of session objects to enhance
 * @returns {Array} - Array of enhanced session objects
 */
export function enhanceSessionsContacts(sessions) {
  if (!Array.isArray(sessions)) {
    return sessions;
  }

  return sessions.map(enhanceSessionContact);
}
